import { useState } from 'react';
import { useProjectStore } from '@/store/projectStore';
import { republishWebApp } from '@/webapp/republish';
import { hostOnSaddle } from '@/webapp/saddleHost';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { Button } from '../../ui';

/**
 * Publishing is the point of the product: it ends in a URL you bookmark.
 *
 * The sequence mirrors the phone's publishWebEdit exactly — assemble and upload, then
 * prefer the stable saddle URL and fall back to the direct upload link — and the result
 * is written back onto the project so a reload still knows where the app lives.
 *
 * Every failure names its own reason. WebAppPublishError distinguishes five, and
 * collapsing them into "publishing failed" would hide the two the person can act on
 * (not signed in, no backend).
 */
export default function PublishPanel({ project }: { project: Project }) {
  const definition = project.definition as AppDefinition;
  const [url, setUrl] = useState<string | null>(definition.webUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function publish(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const { url: uploadUrl, html } = await republishWebApp(definition);
      const hosted = await hostOnSaddle({ ...project, definition }, html);
      const live = hosted?.url ?? uploadUrl;
      await useProjectStore.getState().updateProject(project.id, {
        definition: {
          ...definition,
          webUrl: live,
          ...(hosted ? { hostedAppId: hosted.appId } : {}),
        },
      });
      setUrl(live);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish this app.');
    } finally {
      // Always: a stuck spinner is the failure people actually report.
      setBusy(false);
    }
  }

  async function copy(): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard permission denied — the link is on screen and selectable anyway */
    }
  }

  return (
    <section className="publish-panel">
      <h2 className="type-micro section-label">Publish</h2>
      {url && (
        <p className="type-body publish-url">
          <a href={url} target="_blank" rel="noreferrer">{url}</a>
        </p>
      )}
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
      <div className="publish-actions">
        <Button
          title={url ? 'Publish changes' : 'Publish'}
          variant={url ? 'secondary' : 'primary'}
          onPress={() => void publish()}
          loading={busy}
        />
        {url && <Button title={copied ? 'Copied' : 'Copy link'} variant="ghost" onPress={() => void copy()} />}
      </div>
    </section>
  );
}
